import { useState } from 'react';
import classNames from 'classnames';
import {
  Button,
  InlineNotification,
  Modal,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import { ArrowRight, Compare } from '@carbon/react/icons';
import { CompareSection } from 'components/compare-section';
import { FormattedCount } from 'components/formatted-count';
import { useAreaEditCompare } from './hooks';
import {
  asShape,
  compareSections,
  displayValue,
  shapeFrame,
  shapePath,
  type AreaCompareTableRow,
  type AreaCompareTableSection,
  type AreaShape,
} from './compare-utils';
import styles from './area-compare-sheet.module.scss';

type Side = 'from' | 'to';

interface AreaCompareSheetProps {
  open: boolean;
  /** The workspace's draft. */
  areaGeometryId: number;
  workspaceName: string;
  areaName: string;
  onClose: () => void;
}

interface ShapeThumbProps {
  shape: AreaShape | null;
  other: AreaShape | null;
  side: Side;
}

/**
 * A boundary drawn small, in a frame it shares with the other side of its row so the
 * two read at one scale. The new side shows the published boundary faintly underneath,
 * dashed, so what moved is plain without a map. The published side draws only itself:
 * UAT read the new outline there as the new shape leaking into the old.
 *
 * @param {object} props - The boundary, the other side's, and which side this is.
 * @return {JSX.Element} The thumbnail.
 */
function ShapeThumb({ shape, other, side }: ShapeThumbProps): JSX.Element {
  const frame = shapeFrame([shape, other].filter((s): s is AreaShape => !!s));
  if (!shape || !frame) return <span>—</span>;
  // A tenth of the frame either side, so an edge on the frame is not cut by it.
  const pad = Math.max(frame.width, frame.height) * 0.1;
  const viewBox = `${-pad} ${-pad} ${frame.width + pad * 2} ${frame.height + pad * 2}`;
  return (
    <svg
      className={styles.thumb}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={side === 'from' ? 'Published boundary' : 'Edited boundary'}
    >
      {side === 'to' && other && (
        <path
          className={styles.thumbGhost}
          data-testid="compare-ghost"
          d={shapePath(other, frame)}
          vectorEffect="non-scaling-stroke"
        />
      )}
      <path
        className={side === 'from' ? styles.thumbFrom : styles.thumbTo}
        d={shapePath(shape, frame)}
        fillRule="evenodd"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

interface SideValueProps {
  row: AreaCompareTableRow;
  side: Side;
}

/**
 * One side of a row: the boundary as a thumbnail, a colour as a swatch beside its
 * hex, anything else as text.
 *
 * @param {object} props - The row and which side to draw.
 * @return {JSX.Element} The cell's content.
 */
function SideValue({ row, side }: SideValueProps): JSX.Element {
  const value = row[side];
  if (row.field === 'geojson') {
    return (
      <ShapeThumb
        shape={asShape(value)}
        other={asShape(side === 'from' ? row.to : row.from)}
        side={side}
      />
    );
  }
  const text = displayValue(row.field, value);
  if (row.field === 'display_color' && typeof value === 'string' && value) {
    return (
      <span className={styles.colour}>
        <span className={styles.swatch} style={{ backgroundColor: value }} />
        {text}
      </span>
    );
  }
  return <>{text}</>;
}

/**
 * One section: a collapsible header with its change count, over a Field / Published /
 * Workspace / Action table, as the Well Workspace Compare draws it.
 *
 * @param {object} props - The section.
 * @return {JSX.Element} The section.
 */
function AreaChangesSection({
  section,
}: {
  section: AreaCompareTableSection;
}): JSX.Element {
  return (
    <CompareSection label={section.label} count={section.rows.length}>
      <Table size="lg" className={styles.table}>
        <TableHead>
          <TableRow>
            <TableHeader>Field</TableHeader>
            <TableHeader>Published (previous)</TableHeader>
            <TableHeader>Workspace (new)</TableHeader>
            <TableHeader>Action</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {section.rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.label}</TableCell>
              <TableCell className={styles.from}>
                <SideValue row={row} side="from" />
              </TableCell>
              <TableCell>
                <span className={styles.to}>
                  <ArrowRight className={styles.arrow} aria-hidden />
                  <SideValue row={row} side="to" />
                </span>
              </TableCell>
              <TableCell>
                {row.status === 'new' ? (
                  <Tag type="green" size="sm">
                    Create
                  </Tag>
                ) : (
                  <Tag type="blue" size="sm">
                    Edit
                  </Tag>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CompareSection>
  );
}

/**
 * Compare to Published: what this workspace changes against the area as it is
 * published, ORIGIN on the left and this workspace on the right, as the Well
 * Workspace Compare lays it out.
 *
 * @param {AreaCompareSheetProps} props - The draft, the names, and the close.
 * @return {JSX.Element} The sheet.
 */
export function AreaCompareSheet({
  open,
  areaGeometryId,
  workspaceName,
  areaName,
  onClose,
}: AreaCompareSheetProps): JSX.Element {
  const served = useAreaEditCompare(areaGeometryId, open);
  const sections = compareSections(served.data?.sections ?? []);
  const edits = served.data?.rollup.edits ?? 0;
  const hasOrigin = served.data?.hasOrigin ?? true;

  return (
    <Modal
      open={open}
      className={styles.sheet}
      modalLabel="Area Workspace"
      modalHeading="Compare to Published"
      primaryButtonText="Close"
      size="lg"
      onRequestClose={onClose}
      onRequestSubmit={onClose}
    >
      <div className={styles.sides}>
        <div className={styles.side}>
          <span className={styles.sideBadge}>ORIGIN</span>
          <span className={styles.sideLabel}>
            {hasOrigin ? areaName : 'Nothing published yet'}
            {hasOrigin && <span className={styles.asOf}> · published</span>}
          </span>
        </div>
        <div className={classNames(styles.side, styles.sideTarget)}>
          <span
            className={classNames(styles.sideBadge, styles.sideBadgeTarget)}
          >
            TARGET
          </span>
          <span className={styles.sideLabel}>
            {workspaceName}
            <span className={styles.asOf}> · staged edits</span>
          </span>
        </div>
      </div>

      {/* Loading and failure come before the empty state: an in-flight or failed read
          must never read as "no changes". */}
      {served.isPending ? (
        <SkeletonText paragraph lineCount={4} />
      ) : served.isError ? (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle="Could not load this workspace's changes."
          lowContrast
          hideCloseButton
        />
      ) : edits === 0 ? (
        <p className={styles.empty}>
          No staged edits in this workspace yet — it matches what is published.
        </p>
      ) : (
        <>
          <p className={styles.summary}>
            <FormattedCount value={edits} /> staged edit{edits === 1 ? '' : 's'}
            {hasOrigin
              ? ''
              : ' · new area, nothing published to compare against'}
          </p>
          {sections.map((section) => (
            <AreaChangesSection key={section.key} section={section} />
          ))}
        </>
      )}
    </Modal>
  );
}

interface AreaCompareButtonProps {
  areaGeometryId: number;
  workspaceName: string;
  areaName: string;
}

/**
 * The workspace bar's Compare, with the sheet it opens. The sheet is mounted only
 * while open: a closed Carbon Modal still renders its body into the page.
 *
 * @param {AreaCompareButtonProps} props - The draft and the names the sheet shows.
 * @return {JSX.Element} The button, and the sheet while open.
 */
export function AreaCompareButton({
  areaGeometryId,
  workspaceName,
  areaName,
}: AreaCompareButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        kind="ghost"
        size="md"
        renderIcon={Compare}
        onClick={() => setOpen(true)}
      >
        Compare
      </Button>
      {open && (
        <AreaCompareSheet
          open
          areaGeometryId={areaGeometryId}
          workspaceName={workspaceName}
          areaName={areaName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
